// InstancePromoStores.js
import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Avatar,
  Divider,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  Pagination,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { AuthContext } from "../../../../helpers/AuthContext";
import DataTable from "../../../../components/DataTable";
import CustomerSearchSelectAsync from "../../../../components/CustomerSearchSelectAsync";
import { UITableToolbar } from "../../../../components/ui";
import PromoStoreNavigation, { buildPromoStoreScopeTabs } from "./PromoStoreNavigation";

// ✅ match Projects page bar styles
import "./InstancePromoStores.css";

// Icons
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

/**
 * Build the public URL for a store from domain/path.
 */
function normalizePromoStoreBaseSlug(value = "") {
  return String(value)
    .replace(/^\/+|\/+$/g, "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function isEmailLikePathValue(value = "") {
  const normalized = String(value || "").trim();
  return normalized.includes("@") || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

function getSafePromoStorePathBase(value = "") {
  if (isEmailLikePathValue(value)) return "";
  return normalizePromoStoreBaseSlug(value);
}

const PROMOSTORE_DOMAIN_OPTIONS = [
  "promostores.elitecore.ca",
  "promostores2.elitecore.ca",
];

const PROMOSTORE_STORE_TYPE_OPTIONS = [
  { value: "company_store", label: "Company Store" },
  { value: "pop_up_store", label: "Pop-Up Store" },
  { value: "program_store", label: "Program Store" },
];

function buildStoreUrl(domain, path) {
  if (!domain) return "";
  const hasProtocol = /^https?:\/\//i.test(domain);
  const base = hasProtocol ? domain : `https://${domain}`;

  const safePath = (path || "").trim();
  if (!safePath) return base.replace(/\/+$/, "");

  const withSlashes = safePath.startsWith("/") ? safePath : `/${safePath}`;
  const normalized = withSlashes.endsWith("/") ? withSlashes : `${withSlashes}/`;

  return `${base.replace(/\/+$/, "")}${normalized}`;
}

/**
 * Return a short slug display:
 * - "/promostore-7-29040/" -> "promostore-7-29040"
 */
function getSlugFromPath(path) {
  if (!path) return "";
  return String(path).trim().replace(/^\/+/, "").replace(/\/+$/, "");
}

function stripHtmlAndEntities(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#039;/g, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeDisplayCandidate(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\s+/g, "")
    .replace(/\/+$/g, "");
}

function getCleanPromoStoreTitle(store) {
  const rawTitle =
    typeof store?.title === "object"
      ? store?.title?.rendered || store?.title?.raw || ""
      : store?.title;
  const cleaned = stripHtmlAndEntities(rawTitle);
  if (!cleaned) return "";

  const slug = getSlugFromPath(store?.path);
  const domain = String(store?.domain || "").trim();
  const normalizedTitle = normalizeDisplayCandidate(cleaned);
  const normalizedDomain = normalizeDisplayCandidate(domain);
  const normalizedDomainPath = normalizeDisplayCandidate(`${domain}/${slug}`);

  if (
    normalizedTitle &&
    normalizedTitle !== normalizedDomain &&
    normalizedTitle !== normalizedDomainPath
  ) {
    return cleaned;
  }

  return "";
}

function getPromoStoreDisplayName(store) {
  const shopName = stripHtmlAndEntities(store?.ShopName);
  if (shopName) return shopName;

  return getCleanPromoStoreTitle(store) || getSlugFromPath(store?.path) || "PromoStore";
}

/**
 * Default screenshot provider (public) — WordPress mShots.
 */
function buildScreenshotUrl(pageUrl, width = 1200) {
  if (!pageUrl) return "";
  return `https://s.wordpress.com/mshots/v1/${encodeURIComponent(pageUrl)}?w=${width}`;
}

function safeCustomerDisplay(customer) {
  if (!customer) return "No customer attached";
  return customer.businessName
    ? customer.businessName
    : `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "Customer";
}

function safeCustomerLogoUrl(customer) {
  if (!customer) return "";
  const raw = customer.customUploadedBrandLogo;
  if (!raw || typeof raw !== "string") return "";
  if (raw.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${raw}`;
  return raw;
}

function formatLastSyncedAt(value) {
  if (!value) return "Last synced: unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Last synced: unknown";
  return `Last synced: ${date.toLocaleString()}`;
}

function formatPromoStoreActivityDateTime(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Unknown activity time";
  return date.toLocaleString();
}

function formatPromoStoreActivityRelative(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "unknown";
  return formatDistanceToNow(date, { addSuffix: true });
}

function getPromoStoreActivitySummary(store) {
  const reason = String(store?.lastActivityReason || "PromoStore created").trim() || "PromoStore created";
  const relative = formatPromoStoreActivityRelative(store?.lastActivityAt || store?.createdAt);
  const exact = formatPromoStoreActivityDateTime(store?.lastActivityAt || store?.createdAt);
  const activityTime = new Date(store?.lastActivityAt || store?.createdAt).getTime();
  const elapsedMs = Date.now() - activityTime;
  const isRecent = Number.isFinite(activityTime) && elapsedMs >= 0 && elapsedMs < 60 * 60 * 1000;
  return {
    reason,
    relative,
    exact,
    isRecent,
    isNewOrder: reason === "New order",
  };
}

const totalChipSx = {
  backgroundColor: "var(--ec-status-neutral-bg)",
  color: "var(--ec-status-neutral-text)",
  border: "1px solid var(--ec-status-neutral-border)",
  fontWeight: 600,
  fontSize: "0.72rem",
  height: 22,
};

const showcaseClosedStatusChipSx = {
  backgroundColor: "var(--ec-status-neutral-bg)",
  color: "var(--ec-status-neutral-text)",
  border: "1px solid var(--ec-status-neutral-border)",
  fontWeight: 700,
};

function PromoStoreActivityBlock({ store, compact = false }) {
  const activity = getPromoStoreActivitySummary(store);
  const Icon = activity.isRecent ? LocalFireDepartmentRoundedIcon : (activity.isNewOrder ? ReceiptLongRoundedIcon : StorefrontRoundedIcon);

  return (
    <Tooltip title={activity.exact}>
      <Box sx={{ display: "flex", alignItems: compact ? "center" : "flex-start", gap: 1, minWidth: 0 }}>
        <Icon sx={{ fontSize: compact ? 16 : 18, color: activity.isRecent ? "var(--ec-brand-red, #e53935)" : "rgba(17,17,17,0.72)", mt: compact ? 0 : "2px" }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: "#1c2430",
              lineHeight: 1.2,
              whiteSpace: compact ? "nowrap" : "normal",
            }}
          >
            {compact ? `${activity.relative} · ${activity.reason}` : `Last updated: ${activity.relative}`}
          </Typography>
          {!compact ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.2, color: "rgba(28,36,48,0.7)", fontWeight: 700 }}>
              {activity.reason}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Tooltip>
  );
}

function isArchivedStore(store) {
  return store?.archived === true || store?.archived === 1 || store?.archived === "1";
}

function activePromoStores(stores = []) {
  return stores.filter((store) => !isArchivedStore(store));
}

/**
 * My Stores filter:
 * Uses AssignedTo / assignedTo / assigned_to array
 */
function storeAssignedToUser(store, userId) {
  if (!userId) return false;
  const assigned = store?.AssignedTo ?? store?.assignedTo ?? store?.assigned_to ?? [];
  if (!Array.isArray(assigned)) return false;

  const uidStr = String(userId);
  return assigned.some((a) => {
    if (a == null) return false;
    if (typeof a === "string" || typeof a === "number") return String(a) === uidStr;
    if (typeof a === "object") {
      const candidate = a.id ?? a.userId ?? a.user_id ?? a.value;
      return candidate != null && String(candidate) === uidStr;
    }
    return false;
  });
}

const InstancePromoStores = () => {
  const { instanceUniqueId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { authState } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [allStores, setAllStores] = useState([]);
  const [allStoresPage, setAllStoresPage] = useState(1);
  const [allStoresPageSize] = useState(15);
  const [allStoresTotal, setAllStoresTotal] = useState(0);
  const [allStoresTotalPages, setAllStoresTotalPages] = useState(0);
  const [allStoresLoading, setAllStoresLoading] = useState(false);
  const [allStoresQuery, setAllStoresQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [createStoreModalOpen, setCreateStoreModalOpen] = useState(false);
  const [quickActionCustomerId, setQuickActionCustomerId] = useState("");

  // ✅ Copy Projects-style "My / All" tabs
  const [scopeMode, setScopeMode] = useState("my"); // my | all | archived

  // ✅ Single icon button toggle between snapshots/table
  const [viewMode, setViewMode] = useState("grid"); // grid | table

  // Sort filter
  const [sortMode, setSortMode] = useState("activity"); // activity | created
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [, setRelativeTimeTick] = useState(0);
  const canViewAllStores =
    authState.role === "administrator" || authState.role === "finance-rep";
  const canViewArchivedStores = authState.role === "administrator";

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRelativeTimeTick((value) => value + 1);
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/get-promostores`,
        { params: { status: scopeMode === "archived" ? "archived" : "active", sort: sortMode } }
      );

      if (response.data?.success) {
        const sourceStores = scopeMode === "archived" ? response.data.promostores : activePromoStores(response.data.promostores);
        const formatted = sourceStores.map((item) => ({
          ...item,
          id: item.id || item.blog_id,
        }));
        setSites(formatted);
      } else {
        console.error("Error fetching sites:", response.data?.message);
      }
    } catch (err) {
      console.error("Error while fetching sites:", err);
    } finally {
      setLoading(false);
    }
  }, [scopeMode, sortMode]);

  const fetchAllStores = useCallback(
    async ({ page = allStoresPage, query = allStoresQuery } = {}) => {
      setAllStoresLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/get-promostores`,
          {
            params: {
              page,
              pageSize: allStoresPageSize,
              q: query || undefined,
              status: scopeMode === "archived" ? "archived" : "active",
              sort: sortMode,
            },
          }
        );

        if (response.data?.success) {
          const sourceStores = scopeMode === "archived" ? response.data.promostores : activePromoStores(response.data.promostores);
        const formatted = sourceStores.map((item) => ({
            ...item,
            id: item.id || item.blog_id,
          }));
          setAllStores(formatted);
          setAllStoresTotal(response.data.total || 0);
          setAllStoresTotalPages(response.data.totalPages || 0);
        } else {
          console.error("Error fetching paged sites:", response.data?.message);
        }
      } catch (err) {
        console.error("Error while fetching paged sites:", err);
      } finally {
        setAllStoresLoading(false);
      }
    },
    [allStoresPage, allStoresPageSize, allStoresQuery, scopeMode, sortMode]
  );

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    if (scopeMode !== "all" && scopeMode !== "archived") return;
    const handle = setTimeout(() => {
      setAllStoresPage(1);
      setAllStoresQuery(searchInput);
    }, 300);
    return () => clearTimeout(handle);
  }, [scopeMode, searchInput]);

  useEffect(() => {
    if (scopeMode !== "all" && scopeMode !== "archived") return;
    setAllStoresPage(1);
  }, [scopeMode, sortMode]);

  useEffect(() => {
    if (scopeMode !== "all" && scopeMode !== "archived") return;
    fetchAllStores({ page: allStoresPage, query: allStoresQuery });
  }, [fetchAllStores, scopeMode, allStoresPage, allStoresQuery]);

  const searchedSites = useMemo(() => {
    const baseSites = scopeMode === "all" || scopeMode === "archived" ? allStores : sites;
    if (scopeMode === "all" || scopeMode === "archived") return baseSites;
    if (!searchInput) return baseSites;
    const q = searchInput.toLowerCase();

    return baseSites.filter((s) => {
      const domain = (s.domain || "").toLowerCase();
      const path = (s.path || "").toLowerCase();
      const title = (s.title || "").toLowerCase();
      const shopName = (s.ShopName || "").toLowerCase();
      const displayName = getPromoStoreDisplayName(s).toLowerCase();
      const slug = getSlugFromPath(s.path || "").toLowerCase();
      return (
        domain.includes(q) ||
        path.includes(q) ||
        title.includes(q) ||
        shopName.includes(q) ||
        displayName.includes(q) ||
        slug.includes(q)
      );
    });
  }, [allStores, scopeMode, sites, searchInput]);

  const scopedSites = useMemo(() => {
    if (scopeMode === "all") return searchedSites;
    if (scopeMode !== "my") return searchedSites;
    return searchedSites.filter((s) => storeAssignedToUser(s, authState?.id));
  }, [searchedSites, scopeMode, authState?.id]);

  const filteredSites = useMemo(() => [...scopedSites], [scopedSites]);

  const currentScopeTotal = useMemo(() => {
    if (scopeMode === "all" || scopeMode === "archived") {
      return allStoresTotal;
    }

    return filteredSites.length;
  }, [allStoresTotal, filteredSites.length, scopeMode]);

  const promoStoreScopeTabs = useMemo(
    () => buildPromoStoreScopeTabs({ canViewAllStores, canViewArchivedStores }),
    [canViewAllStores, canViewArchivedStores]
  );

  const handleOpenCreateStoreModal = () => setCreateStoreModalOpen(true);
  const handleCloseCreateStoreModal = () => {
    setCreateStoreModalOpen(false);
    setQuickActionCustomerId("");
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const quickAction = String(params.get("quickAction") || "").toLowerCase();
    if (quickAction !== "launch-promostore") return;

    setQuickActionCustomerId(String(params.get("customerId") || ""));
    setCreateStoreModalOpen(true);

    params.delete("quickAction");
    params.delete("customerId");
    const remaining = params.toString();
    history.replace({
      pathname: location.pathname,
      search: remaining ? `?${remaining}` : "",
    });
  }, [history, location.pathname, location.search]);
  const handleStoreCreated = async () => {
    if (scopeMode === "all" || scopeMode === "archived") {
      await fetchAllStores({ page: allStoresPage, query: allStoresQuery });
      return;
    }
    await fetchSites();
  };

  const handleRefreshStores = useCallback(async () => {
    if (scopeMode === "all" || scopeMode === "archived") {
      await fetchAllStores({ page: allStoresPage, query: allStoresQuery });
      return;
    }

    await fetchSites();
  }, [allStoresPage, allStoresQuery, fetchAllStores, fetchSites, scopeMode]);

  const handleScopeChange = useCallback(
    (nextScope) => {
      if (!nextScope || nextScope === scopeMode) return;
      if (nextScope === "marketplace") {
        history.push(`/instance/${instanceUniqueId}/sales/promostores/marketplace`);
        return;
      }
      setScopeMode(nextScope);
      if (nextScope === "all" || nextScope === "archived") {
        setAllStoresPage(1);
      }
    },
    [history, instanceUniqueId, scopeMode]
  );

  const searchPlaceholder =
    scopeMode === "my"
      ? "Search store name or domain..."
      : "Search store name, customer or domain...";

  const columns = useMemo(
    () => [
      { field: "id", headerName: "Blog ID", width: 90 },
      {
        field: "displayName",
        headerName: "Store Name",
        width: 220,
        valueGetter: (value, row) => getPromoStoreDisplayName(row),
      },
      { field: "domain", headerName: "Domain", width: 220 },
      {
        field: "slug",
        headerName: "Slug",
        width: 240,
        valueGetter: (value, row) => getSlugFromPath(row?.path),
      },
      {
        field: "customer",
        headerName: "Customer",
        width: 280,
        renderCell: (params) => {
          const customerArr = params.row.customer;
          if (!customerArr || customerArr.length === 0) {
            return <span className="customer-none-text">No customer attached</span>;
          }
          const customer = customerArr[0];
          const displayName = safeCustomerDisplay(customer);
          const logoUrl = safeCustomerLogoUrl(customer);

          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="Customer Logo"
                  style={{
                    width: "30px",
                    height: "30px",
                    marginRight: "8px",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                  className="supplier-logo-img custom-logo"
                />
              )}
              <span className="customer-display-text">{displayName}</span>
            </div>
          );
        },
      },
      {
        field: "lastActivity",
        headerName: "Last Activity",
        width: 240,
        sortable: false,
        renderCell: (params) => <PromoStoreActivityBlock store={params.row} compact />,
      },
      {
        field: "isSynced",
        headerName: "Sync Status",
        width: 160,
        renderCell: (params) => {
          const isSynced = Boolean(params.row.isSynced);
          const label = isSynced ? "Synced" : "Out of sync";
          const chip = (
            <Chip
              label={label}
              color={isSynced ? "success" : "warning"}
              size="small"
              sx={{ fontWeight: 700 }}
            />
          );
          const tooltipTitle = formatLastSyncedAt(params.row.lastSyncedAt);
          return <Tooltip title={tooltipTitle}>{chip}</Tooltip>;
        },
      },
      {
        field: "action",
        headerName: "Action",
        width: 160,
        renderCell: (params) => (
          <div className="cellAction">
            <a
              className="viewButton"
              target="_blank"
              rel="noopener noreferrer"
              href={`/instance/${instanceUniqueId}/sales/promostores/${params.row.id}`}
            >
              View Details
            </a>
          </div>
        ),
      },
    ],
    [instanceUniqueId]
  );

  return (
    <div className="instance-dashboard instance-webstores-page instance-webstores-page-home">
      <InstanceLeftSidebar />

      <div className="rightSide">
        <div className="options-container fade-in-effect-usability">
          <div className="options-inner-container">
            <UITableToolbar
              className="promostores-toolbar"
              sx={{ p: 0, backgroundColor: "background.paper",

                    borderColor: "divider",
                    borderTop: "3px solid",
                    borderLeft: 0,
                    borderRight: 0,
                    borderBottom: 0,
                    borderTopColor: "primary.main"

               }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
                sx={{ px: 2.5, pt: 1.75 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: "text.primary", lineHeight: 1 }}
                  >
                    PromoStores
                  </Typography>

                  <Chip label={`${currentScopeTotal} total`} size="small" sx={totalChipSx} />

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: 20, alignSelf: "center" }}
                  />

                  <PromoStoreNavigation
                    items={promoStoreScopeTabs}
                    activeKey={scopeMode}
                    onSelect={handleScopeChange}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={viewMode}
                    onChange={(_, value) => {
                      if (!value) return;
                      setViewMode(value);
                    }}
                    sx={{
                      mr: 0.5,
                      "& .MuiToggleButton-root": {
                        height: 32,
                        px: 1.25,
                        textTransform: "none",
                        fontWeight: 700,
                        borderColor: "divider",
                      },
                      "& .Mui-selected": {
                        color: "var(--ec-color-surface) !important",
                        backgroundColor: "var(--ec-color-primary) !important",
                      },
                    }}
                  >
                    <ToggleButton value="grid" aria-label="Showcase view">
                      <GridViewRoundedIcon sx={{ fontSize: 17, mr: 0.6 }} />
                      Showcase
                    </ToggleButton>
                    <ToggleButton value="table" aria-label="Table view">
                      <TableRowsRoundedIcon sx={{ fontSize: 17, mr: 0.6 }} />
                      Table
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Tooltip title="Filter / Sort">
                    <IconButton
                      size="small"
                      onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                      sx={{ color: "text.secondary" }}
                    >
                      <FilterListRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Refresh stores">
                    <IconButton
                      size="small"
                      onClick={handleRefreshStores}
                      sx={{ color: "text.secondary" }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 0.5, height: 24, alignSelf: "center" }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateStoreModal}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Create Web Store
                  </Button>
                </Stack>
              </Stack>

              <Divider sx={{ mt: 1.75, mb: 0 }} />

              <Stack
                direction="row"
                spacing={1.5}
                mt={1.75}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
                sx={{ px: 2.5, pb: 1.75 }}
              >
                <TextField
                  placeholder={searchPlaceholder}
                  size="small"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (scopeMode === "all" || scopeMode === "archived")) {
                      e.preventDefault();
                      setAllStoresPage(1);
                      setAllStoresQuery(searchInput);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchInput("")} edge="end">
                          <ClearIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    flex: 2,
                    minWidth: 280,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--ec-input-bg)",
                    },
                  }}
                />
              </Stack>
            </UITableToolbar>

            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={() => setFilterAnchorEl(null)}
              PaperProps={{
                sx: {
                  borderRadius: "14px",
                  mt: 1,
                  minWidth: 240,
                  border: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "0 12px 35px rgba(0,0,0,0.14)",
                  overflow: "hidden",
                },
              }}
            >
              <MenuItem
                selected={sortMode === "activity"}
                onClick={() => {
                  setSortMode("activity");
                  setFilterAnchorEl(null);
                }}
              >
                Recent Activity
              </MenuItem>
              <MenuItem
                selected={sortMode === "created"}
                onClick={() => {
                  setSortMode("created");
                  setFilterAnchorEl(null);
                }}
              >
                Newest Created
              </MenuItem>
            </Menu>
            <Box sx={{ mt: 0, width: "100%" }}>
              {(scopeMode === "all" || scopeMode === "archived" ? allStoresLoading : loading) ? (
                <Box className="promostores-page-loader">
                  <CircularProgress />
                </Box>
              ) : viewMode === "grid" ? (
                <WebStoreSnapshotGrid instanceUniqueId={instanceUniqueId} sites={filteredSites} />
              ) : (
                <DataTable
                  columns={columns}
                  rows={filteredSites}
                  page={scopeMode === "all" || scopeMode === "archived" ? allStoresPage : undefined}
                  pageSize={scopeMode === "all" || scopeMode === "archived" ? allStoresPageSize : undefined}
                  rowCount={scopeMode === "all" || scopeMode === "archived" ? allStoresTotal : undefined}
                  onPageChange={scopeMode === "all" || scopeMode === "archived" ? setAllStoresPage : undefined}
                />
              )}
            </Box>
            {(scopeMode === "all" || scopeMode === "archived") && viewMode === "grid" && allStoresTotalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={allStoresTotalPages}
                  page={allStoresPage}
                  onChange={(_event, value) => setAllStoresPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </div>
        </div>
      </div>

      <CreateWebStoreModal
        open={createStoreModalOpen}
        onClose={handleCloseCreateStoreModal}
        onStoreCreated={handleStoreCreated}
        instanceUniqueId={instanceUniqueId}
        authState={authState}
        defaultCustomerId={quickActionCustomerId || ""}
      />
    </div>
  );
};

export default InstancePromoStores;

/**
 * Snapshot Grid
 * - Shows friendly store names while preserving URL routing from domain/path.
 */
const WebStoreSnapshotGrid = ({ instanceUniqueId, sites }) => {
  if (!sites || sites.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">No web stores found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
        gap: 2.5,
        pb: 2,
      }}
    >
      {sites.map((site) => {
        const storeUrl = buildStoreUrl(site.domain, site.path);
        const displayName = getPromoStoreDisplayName(site);
        const isClosed = String(site.status || "").toLowerCase() === "closed";

        const screenshotUrl =
          site.screenshotUrl ||
          site.snapshotUrl ||
          site.previewImageUrl ||
          buildScreenshotUrl(storeUrl, 1200);

        const customer = Array.isArray(site.customer) && site.customer.length > 0 ? site.customer[0] : null;
        const customerName = safeCustomerDisplay(customer);
        const customerLogo = safeCustomerLogoUrl(customer);

        return (
          <Card
            key={site.id}
            elevation={0}
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: "16px",
              overflow: "hidden",
              transition: "transform 140ms ease, box-shadow 140ms ease",
              background: "rgba(255,255,255,0.8)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 14px 40px rgba(0,0,0,0.14)",
              },
            }}
          >
            <Box sx={{ position: "relative" }}>
              <CardMedia
                component="img"
                image={screenshotUrl}
                alt={`${displayName || site.domain || "Store"} snapshot`}
                sx={{
                  height: 220,
                  width: "100%",
                  objectFit: "cover",
                  backgroundColor: "rgba(0,0,0,0.02)",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </Box>

            <CardContent sx={{ p: 1.7 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                {displayName}
              </Typography>

              <Box
                sx={{
                  mt: 1.1,
                  mb: 1.05,
                  p: 1,
                  borderRadius: "12px",
                  background: "linear-gradient(180deg, rgba(17,17,17,0.04), rgba(17,17,17,0.02))",
                  border: "1px solid rgba(17,17,17,0.06)",
                }}
              >
                <PromoStoreActivityBlock store={site} />
              </Box>

              <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
                <Chip
                  label={isClosed ? "Closed" : "Live"}
                  color={isClosed ? undefined : "success"}
                  size="small"
                  sx={isClosed ? showcaseClosedStatusChipSx : { fontWeight: 700 }}
                />
                <Tooltip title={formatLastSyncedAt(site.lastSyncedAt)}>
                  <Chip
                    label={site.isSynced ? "Synced" : "Out of sync"}
                    color={site.isSynced ? "success" : "warning"}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
              </Stack>

              <Divider sx={{ my: 1.2 }} />

              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar src={customerLogo || undefined} sx={{ width: 30, height: 30 }}>
                  {customerName?.[0] || "C"}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 900, opacity: 0.9 }}>
                  {customerName}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1.4 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => window.open(storeUrl || "#", "_blank", "noopener,noreferrer")}
                  disabled={!storeUrl}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 950,
                    borderColor: "rgba(0,0,0,0.18)",
                    color: "#111",
                    background: "rgba(255,255,255,0.75)",
                    "&:hover": {
                      borderColor: "rgba(0,0,0,0.30)",
                      background: "rgba(255,255,255,0.95)",
                    },
                  }}
                >
                  Open Store
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    window.location.href = `/instance/${instanceUniqueId}/sales/promostores/${site.id}`;
                  }}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 980,
                    background: "#111",
                    color: "white",
                    boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                    "&:hover": {
                      background: "#000",
                      boxShadow: "0 14px 34px rgba(0,0,0,0.22)",
                    },
                  }}
                >
                  Details
                </Button>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

const CreateWebStoreModal = ({ open, onClose, onStoreCreated, instanceUniqueId, authState, defaultCustomerId = "" }) => {
  const history = useHistory();
  const initialValues = {
    domain: "",
    pathBaseInput: "",
    title: "",
    storeType: "",
    expiryDate: "",
    expiryTime: "23:59",
    creditCard: true,
    calculateTaxes: true,
    assignedCustomer: defaultCustomerId || "",
  };

  const validationSchema = Yup.object({
    domain: Yup.string()
      .oneOf(PROMOSTORE_DOMAIN_OPTIONS, "Please select a domain.")
      .required("Please select a domain."),
    pathBaseInput: Yup.string()
      .transform((value) => getSafePromoStorePathBase(value) || undefined)
      .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
        message: "Path base must be lowercase, alphanumeric, and may include hyphens",
        excludeEmptyString: true,
      })
      .min(3, "Must be at least 3 characters")
      .max(30, "Cannot exceed 30 characters")
      .notRequired(),
    title: Yup.string().required("Title is required"),
    storeType: Yup.string()
      .oneOf(PROMOSTORE_STORE_TYPE_OPTIONS.map((option) => option.value), "Please select a valid store type")
      .required("Store Type is required"),
    expiryDate: Yup.string()
      .nullable()
      .test("valid-pop-up-expiry-date", "Please enter a valid Pop-Up End Date", function (value) {
        if (this.parent.storeType !== "pop_up_store" || !value) return true;
        return !Number.isNaN(new Date(value).getTime());
      }),
    assignedCustomer: Yup.string().required("Please pick a customer"),
  });
  const pathAvailabilityTimeoutRef = useRef(null);
  const pathAvailabilityRequestRef = useRef(0);
  const [pathAvailability, setPathAvailability] = useState({ status: "idle", message: "" });

  useEffect(() => () => {
    if (pathAvailabilityTimeoutRef.current) clearTimeout(pathAvailabilityTimeoutRef.current);
  }, []);

  const getPreviewSlug = (values) => (
    getSafePromoStorePathBase(values.pathBaseInput) ||
    getSafePromoStorePathBase(values.title) ||
    "customer-name"
  );

  const checkPathAvailability = useCallback((domain, rawPathBase, setFieldError, { immediate = false } = {}) => {
    if (pathAvailabilityTimeoutRef.current) clearTimeout(pathAvailabilityTimeoutRef.current);

    const normalizedDomain = String(domain || "").trim().toLowerCase();
    const safePathBase = getSafePromoStorePathBase(rawPathBase);
    if (!PROMOSTORE_DOMAIN_OPTIONS.includes(normalizedDomain) || !safePathBase) {
      pathAvailabilityRequestRef.current += 1;
      setPathAvailability({ status: "idle", message: "" });
      setFieldError("pathBaseInput", undefined);
      return;
    }

    setFieldError("pathBaseInput", undefined);
    if (!immediate) setPathAvailability({ status: "idle", message: "" });

    const runCheck = async () => {
      const requestId = pathAvailabilityRequestRef.current + 1;
      pathAvailabilityRequestRef.current = requestId;
      setPathAvailability({ status: "checking", message: "Checking URL path..." });

      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/path-availability`,
          {
            params: { domain: normalizedDomain, pathBase: safePathBase },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );

        if (requestId !== pathAvailabilityRequestRef.current) return;

        if (resp.data?.available) {
          setPathAvailability({ status: "available", message: "This URL path is available." });
          setFieldError("pathBaseInput", undefined);
          return;
        }

        const message = resp.data?.message || "This URL path already exists. Please choose a different one.";
        const status = resp.data?.code === "PROMOSTORE_PATH_INVALID" ? "invalid" : "taken";
        setPathAvailability({ status, message });
        if (status === "taken") setFieldError("pathBaseInput", message);
      } catch (error) {
        if (requestId === pathAvailabilityRequestRef.current) {
          setPathAvailability({ status: "idle", message: "" });
        }
      }
    };

    if (immediate) {
      runCheck();
      return;
    }

    pathAvailabilityTimeoutRef.current = setTimeout(runCheck, 350);
  }, []);


  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError, setFieldTouched }) => {
    try {
      const currentUserId = authState?.id;
      const currentAssignedUser = currentUserId
        ? {
            id: currentUserId,
            role: authState?.role || "customer",
            first_name: authState?.first_name || "",
            last_name: authState?.last_name || "",
            profilePicture: authState?.profilePicture || "",
          }
        : null;
      const normalizedPathBase = getSafePromoStorePathBase(values.pathBaseInput);
  
      const resp = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/create-promostore`,
        {
          domain: values.domain,
          instanceUniqueId,
          title: values.title,
          storeType: values.storeType,
          expiryDate: values.storeType === "pop_up_store" && values.expiryDate ? values.expiryDate : null,
          expiryTime: "23:59",
          calculateTaxes: values.calculateTaxes,
          TemplateConfig: {
            payment: {
              creditCard: values.creditCard,
            },
          },
          attachedCustomers: [values.assignedCustomer],
          AssignedTo: currentAssignedUser ? [currentAssignedUser] : [],
          ...(normalizedPathBase ? { pathBase: normalizedPathBase } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`, // ✅ match your system
          },
        }
      );
  
      if (resp.data?.success) {
        alert(`Store created successfully at ${resp.data?.path || "the generated PromoStore path"}.`);
        await onStoreCreated();
        onClose();
        resetForm();
        const newPromoStoreId = resp.data?.promoStoreId || resp.data?.promostore?.id || resp.data?.promostore?.PromoStoreId;
        if (newPromoStoreId) {
          history.push(`/instance/${instanceUniqueId}/sales/promostores/${newPromoStoreId}`);
        }
      } else {
        alert("Error creating store: " + resp.data.message);
      }
    } catch (err) {
      console.error("Error creating web store", err);
      const errorData = err?.response?.data;
      const message = errorData?.message || "Error creating store. Please try again.";

      if (err?.response?.status === 409 || errorData?.code === "PROMOSTORE_PATH_TAKEN") {
        setFieldTouched("pathBaseInput", true);
        setFieldError("pathBaseInput", message);
        alert(message);
        return;
      }

      alert(message);
    } finally {
      setSubmitting(false);
    }
  };  

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth className="create-order-dialog create-promostore-dialog">
      <DialogTitle className="create-order-dialog-title">Create New PromoStore</DialogTitle>
      <DialogContent className="create-order-dialog-content">
        <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, isSubmitting, setFieldValue, setFieldError, setFieldTouched, touched, errors }) => {
            const hasCustomer = Boolean(values.assignedCustomer);
            const previewSlug = getPreviewSlug(values);
            const isPathAvailabilityBlocking = pathAvailability.status === "checking" || pathAvailability.status === "taken";
            return (
              <Form className="create-order-form">
                <div className="create-order-field-container">
                  <CustomerSearchSelectAsync
                    instanceUniqueId={instanceUniqueId}
                    value={values.assignedCustomer}
                    onChange={(custId) => setFieldValue("assignedCustomer", custId || "")}
                    onSelectCustomer={(cust) => {
                      if (!cust) return;
                      const selectedDomain = String(cust.defaultDomain || "").trim().toLowerCase();
                      setFieldValue(
                        "domain",
                        PROMOSTORE_DOMAIN_OPTIONS.includes(selectedDomain) ? selectedDomain : ""
                      );

                      const displayTitle =
                        cust.businessName || `${cust.firstName || ""} ${cust.lastName || ""}`.trim();
                      setFieldValue("pathBaseInput", "");
                      setPathAvailability({ status: "idle", message: "" });
                      setFieldValue("title", displayTitle);
                    }}
                    label="Customer Account"
                  />
                  {touched.assignedCustomer && errors.assignedCustomer ? (
                    <FormHelperText error>
                      <ErrorMessage name="assignedCustomer" />
                    </FormHelperText>
                  ) : null}
                </div>

                <div className="create-order-field-container">
                  <TextField
                    select
                    label="Store Type"
                    name="storeType"
                    value={values.storeType}
                    className="create-order-input"
                    fullWidth
                    error={Boolean(touched.storeType && errors.storeType)}
                    helperText={touched.storeType && errors.storeType ? errors.storeType : null}
                    onChange={(event) => {
                      const nextStoreType = event.target.value;
                      setFieldValue("storeType", nextStoreType);
                      if (nextStoreType !== "pop_up_store") {
                        setFieldValue("expiryDate", "");
                      }
                    }}
                  >
                    {PROMOSTORE_STORE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                {values.storeType === "pop_up_store" && (
                  <div className="create-order-field-container">
                    <Field
                      as={TextField}
                      label="Pop-Up End Date"
                      name="expiryDate"
                      className="create-order-input"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(touched.expiryDate && errors.expiryDate)}
                      helperText={
                        touched.expiryDate && errors.expiryDate
                          ? errors.expiryDate
                          : "Optional. Checkout closes at 11:59 PM on this date in the company timezone."
                      }
                    />
                  </div>
                )}

                <div className="create-order-field-container">
                  <Field name="pathBaseInput">
                    {({ field }) => {
                      const fieldError = touched.pathBaseInput && errors.pathBaseInput;
                      const availabilityMessage = pathAvailability.message;
                      const pathStatusStyle = {
                        display: "inline-block",
                        marginTop: "4px",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontWeight: 600,
                      };
                      const helperText = pathAvailability.status === "available" ? (
                        <span style={{
                          ...pathStatusStyle,
                          color: "#2e7d32",
                          backgroundColor: "#edf7ed",
                          border: "1px solid #2e7d32",
                        }}>
                          This URL path is available.
                        </span>
                      ) : (fieldError || pathAvailability.status === "taken") ? (
                        <span style={{
                          ...pathStatusStyle,
                          color: "#d32f2f",
                          backgroundColor: "#fdecea",
                          border: "1px solid #d32f2f",
                        }}>
                          {fieldError || "This URL path is already taken."}
                        </span>
                      ) : availabilityMessage || null;

                      return (
                        <TextField
                          {...field}
                          label="URL Path (optional)"
                          className="create-order-input"
                          fullWidth
                          placeholder="Type your own, or leave blank for auto generation."
                          autoComplete="off"
                          inputProps={{
                            autoComplete: "off",
                            "data-lpignore": "true",
                            "data-1p-ignore": "true",
                          }}
                          error={Boolean(fieldError || pathAvailability.status === "taken")}
                          helperText={helperText}
                          FormHelperTextProps={{
                            sx: {
                              color: pathAvailability.status === "checking"
                                ? "text.secondary"
                                : undefined,
                            },
                          }}
                          onChange={(event) => {
                            field.onChange(event);
                            setFieldValue("pathBaseInput", event.target.value);
                            checkPathAvailability(values.domain, event.target.value, setFieldError);
                          }}
                          onBlur={(event) => {
                            field.onBlur(event);
                            setFieldTouched("pathBaseInput", true);
                            checkPathAvailability(values.domain, event.target.value, setFieldError, { immediate: true });
                          }}
                        />
                      );
                    }}
                  </Field>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontStyle: "italic" }}>
                    Store URL
                    <br />
                    We’ll create the final link using the customer name, or you can type a custom URL Path above.
                    <br />
                    {values.domain ? (
                      <>Example: <strong>{values.domain}/{previewSlug}/</strong></>
                    ) : (
                      <>Select a domain to preview the final Store URL.</>
                    )}
                  </Typography>
                </div>

                {hasCustomer && (
                  <>
                    <div className="create-order-field-container">
                      <TextField
                        select
                        label="Domain"
                        name="domain"
                        value={values.domain}
                        className="create-order-input"
                        fullWidth
                        error={Boolean(touched.domain && errors.domain)}
                        helperText={touched.domain && errors.domain ? errors.domain : null}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => selected || (
                            <span style={{ color: "rgba(0, 0, 0, 0.6)" }}>Select domain</span>
                          ),
                        }}
                        onChange={(event) => {
                          setFieldValue("domain", event.target.value);
                          setPathAvailability({ status: "idle", message: "" });
                          const safePathBase = getSafePromoStorePathBase(values.pathBaseInput);
                          if (safePathBase) {
                            checkPathAvailability(event.target.value, safePathBase, setFieldError);
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select domain
                        </MenuItem>
                        {PROMOSTORE_DOMAIN_OPTIONS.map((domainOption) => (
                          <MenuItem key={domainOption} value={domainOption}>
                            {domainOption}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>

                    <div className="create-order-field-container">
                      <Field
                        as={TextField}
                        label="Title"
                        name="title"
                        className="create-order-input"
                        fullWidth
                        error={Boolean(touched.title && errors.title)}
                        helperText={touched.title && errors.title ? errors.title : null}
                      />
                    </div>

                    <DialogActions>
                      <Button onClick={onClose} color="secondary">
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitting || !values.assignedCustomer || !values.storeType || isPathAvailabilityBlocking}
                        onClick={() => {
                          if (!values.domain) setFieldTouched("domain", true, true);
                        }}
                      >
                        Create Store
                      </Button>
                    </DialogActions>
                  </>
                )}

                {!hasCustomer && (
                  <DialogActions>
                    <Button onClick={onClose} color="secondary">
                      Cancel
                    </Button>
                  </DialogActions>
                )}
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
